// services/frontend/src/pages/SignupStep2Page.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SignupFixedBottomButton from '../components/signup/SignupFixedBottomButton';
import SignupPageLayout from '../components/signup/SignupPageLayout';
import { clearSignupDraft, getSignupDraft, patchSignupDraft } from '../utils/signupDraft';

// [회원가입 UI 스타일]
// - Step3(주소검색)에서 사용 중인 모달 스타일을 Step2(약관 보기)에서도 재사용합니다.
import './SignUp.css';

/**
 * [Why]
 * RN 앱과 동일한 UX의 웹 회원가입 2단계(약관 동의) 화면입니다.
 * - 필수 약관 2개 + 선택(마케팅) 1개를 처리합니다.
 *
 * [How]
 * - 동의 결과는 sessionStorage(signup_draft_v1)에 저장합니다.
 */
export default function SignupStep2Page() {
  const navigate = useNavigate();
  const draft = useMemo(() => getSignupDraft() || {}, []);

  useEffect(() => {
    // [회원가입 UX]
    // - Step1 기본정보가 없으면 약관 화면으로 직접 진입할 수 없도록 방지
    const latest = getSignupDraft() || {};
    if (!latest.email || !latest.password) {
      // [테스트/운영 편의]
      // - 이전 테스트/진행 상태가 남아 잘못된 화면으로 진입하는 경우를 방지하기 위해
      //   Step1 필수 데이터가 없으면 draft를 초기화하고 Step1로 돌려보냅니다.
      clearSignupDraft();
      navigate('/signup/step1', { replace: true });
    }
  }, [navigate]);

  const [agreeTerms, setAgreeTerms] = useState(!!draft.agreeTerms);
  const [agreePrivacy, setAgreePrivacy] = useState(!!draft.agreePrivacy);
  const [agreeMarketing, setAgreeMarketing] = useState(!!draft.agreeMarketing);
  const [submitTried, setSubmitTried] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [termsModalTitle, setTermsModalTitle] = useState('');
  const [termsModalUrl, setTermsModalUrl] = useState('');
  const [termsModalHtml, setTermsModalHtml] = useState('');
  const [termsModalLoading, setTermsModalLoading] = useState(false);
  const [termsModalLoadError, setTermsModalLoadError] = useState('');

  const allRequiredAgreed = agreeTerms && agreePrivacy;
  const allAgreed = agreeTerms && agreePrivacy && agreeMarketing;

  // [회원가입 UX]
  // - 필수 약관이 2개이므로, 어떤 항목이 누락되었는지 구체적으로 안내합니다.
  const missingRequiredItems = useMemo(() => {
    const items = [];
    if (!agreeTerms) items.push('이용약관 동의');
    if (!agreePrivacy) items.push('개인정보 처리방침 동의');
    return items;
  }, [agreeTerms, agreePrivacy]);

  const toggleAll = () => {
    const next = !allAgreed;
    setAgreeTerms(next);
    setAgreePrivacy(next);
    setAgreeMarketing(next);
  };

  const goNext = () => {
    setSubmitTried(true);
    if (!allRequiredAgreed) return;

    patchSignupDraft({
      agreeTerms,
      agreePrivacy,
      agreeMarketing,
    });

    navigate('/signup/step3');
  };

  const openTermsModal = (type) => {
    // [약관 보기]
    // - 약관 문서는 정적 HTML(public)로 제공되며, 모달의 iframe으로 로드합니다.
    // - type별 파일명은 /public/terms 하위에 고정합니다.
    const map = {
      // [정적 서빙 호환]
      // - serve의 cleanUrls(.html -> 무확장자 리다이렉트)로 인해 정적 약관이 SPA(index.html)로 폴백될 수 있어,
      //   컨테이너 실행 시 serve.json(cleanUrls=false)을 사용하도록 구성합니다.
      // - 따라서 약관은 원래 의도대로 .html 경로를 직접 요청합니다.
      terms: { title: 'AI 닥터큐 서비스 이용약관', url: '/terms/service-terms.html', fallbackUrl: '/terms/service-terms.html' },
      privacy: { title: '큐브드엠 서비스 개인정보 처리방침', url: '/terms/privacy-policy.html', fallbackUrl: '/terms/privacy-policy.html' },
      marketing: { title: '마케팅 정보 이용 동의', url: '/terms/marketing-consent.html', fallbackUrl: '/terms/marketing-consent.html' },
    };
    const target = map[type];
    if (!target) return;

    // [운영/개발 공통 안정화]
    // - 일부 배포 환경에서 정적 파일 경로 처리(404/SPA 폴백) 또는 보안 헤더로 인해
    //   iframe src 로딩이 비어 보일 수 있어, HTML을 fetch로 받아 srcDoc으로 주입합니다.
    setTermsModalHtml('');
    setTermsModalLoadError('');
    setTermsModalLoading(true);
    // [캐시/리디렉션 이슈 대응]
    // - serve 등 일부 정적 서버는 /terms/*.html 요청을 301로 리디렉션한 뒤, 캐시가 있으면 304로 응답할 수 있습니다.
    // - 304는 응답 바디가 비어 있어(fetch().text() 결과가 ''), srcDoc에 주입하면 iframe이 흰 화면으로 보입니다.
    // - 따라서 cache buster 파라미터를 붙이고, fetch 캐시를 끕니다.
    const cacheBustedUrl = `${target.url}?v=${Date.now()}`;
    const cacheBustedFallbackUrl = `${target.fallbackUrl}?v=${Date.now()}`;

    setTermsModalTitle(target.title);
    setTermsModalUrl(cacheBustedUrl);
    setTermsModalOpen(true);

    const fetchTermsHtml = (url) =>
      fetch(url, { cache: 'no-store', redirect: 'follow' }).then(async (res) => {
        if (!res.ok) {
          throw new Error(`약관 문서를 불러오지 못했습니다. (HTTP ${res.status})`);
        }
        const text = await res.text();
        if (!text || text.trim().length === 0) {
          throw new Error('약관 문서를 불러왔으나 내용이 비어 있습니다. (캐시/리디렉션 또는 서버 설정 확인 필요)');
        }
        // [문제 진단]
        // - 정적 약관 파일이 빌드 산출물에 없으면 serve(-s)가 index.html로 폴백할 수 있습니다.
        //   이 경우 srcDoc에는 <div id="root"></div>만 존재하여 iframe이 빈 화면처럼 보입니다.
        if (text.includes('id="root"') && text.includes('<div') && !text.includes('AI 닥터큐')) {
          throw new Error('약관 문서를 찾을 수 없습니다. (정적 파일 미배포 또는 경로 설정 오류)');
        }
        return text;
      });

    fetchTermsHtml(cacheBustedUrl)
      .catch(() => fetchTermsHtml(cacheBustedFallbackUrl))
      .then(async (res) => {
        setTermsModalHtml(res);
        setTermsModalLoading(false);
      })
      .catch((e) => {
        setTermsModalLoadError(String(e?.message || '약관 문서를 불러오지 못했습니다.'));
        setTermsModalLoading(false);
      });
  };

  const handleViewClick = (e, type) => {
    // [회원가입 UX]
    // - label 내부에 버튼이 있어 클릭 이벤트가 체크박스 토글로 전파될 수 있으므로 차단합니다.
    e.preventDefault();
    e.stopPropagation();
    openTermsModal(type);
  };

  return (
    <SignupPageLayout
      step={2}
      title="회원가입"
      onBack={() => navigate(-1)}
      bottomFixed={(
        <SignupFixedBottomButton
          label="다음"
          onClick={goNext}
          disabled={!allRequiredAgreed}
        />
      )}
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>약관 동의</div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, border: '1px solid #e5e7eb', borderRadius: 12 }}>
          <input type="checkbox" checked={allAgreed} onChange={toggleAll} />
          <div style={{ display: 'grid', gap: 2 }}>
            <div style={{ fontWeight: 600 }}>전체 동의</div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>필수/선택 항목을 모두 동의합니다.</div>
          </div>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, border: '1px solid #e5e7eb', borderRadius: 12 }}>
          <input type="checkbox" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} />
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 600 }}>[필수] 이용약관 동의</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>서비스 이용을 위해 필요합니다.</div>
            </div>
            <button
              type="button"
              onClick={(e) => handleViewClick(e, 'terms')}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#6b7280',
                fontSize: 12,
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              보기
            </button>
          </div>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, border: '1px solid #e5e7eb', borderRadius: 12 }}>
          <input type="checkbox" checked={agreePrivacy} onChange={(e) => setAgreePrivacy(e.target.checked)} />
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 600 }}>[필수] 개인정보 처리방침 동의</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>개인정보 수집/이용에 대한 동의입니다.</div>
            </div>
            <button
              type="button"
              onClick={(e) => handleViewClick(e, 'privacy')}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#6b7280',
                fontSize: 12,
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              보기
            </button>
          </div>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, border: '1px solid #e5e7eb', borderRadius: 12 }}>
          <input type="checkbox" checked={agreeMarketing} onChange={(e) => setAgreeMarketing(e.target.checked)} />
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 600 }}>[선택] 마케팅 정보 수신 동의</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>이벤트/혜택 안내를 받을 수 있습니다.</div>
            </div>
            <button
              type="button"
              onClick={(e) => handleViewClick(e, 'marketing')}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#6b7280',
                fontSize: 12,
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              보기
            </button>
          </div>
        </label>

        {submitTried && !allRequiredAgreed && (
          <div style={{ color: '#b91c1c', fontSize: 13 }}>
            필수 약관에 동의해 주세요.
            {missingRequiredItems.length > 0 ? (
              <div style={{ marginTop: 6, color: '#b91c1c' }}>
                미동의 항목: {missingRequiredItems.join(', ')}
              </div>
            ) : null}
          </div>
        )}
      </div>

      {termsModalOpen && (
        <div className="signup-modal-overlay" onClick={() => setTermsModalOpen(false)}>
          <div className="signup-modal signup-terms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="signup-modal-header">
              <div className="signup-modal-title">{termsModalTitle}</div>
              <button
                type="button"
                className="signup-modal-close"
                onClick={() => setTermsModalOpen(false)}
              >
                닫기
              </button>
            </div>
            <div className="signup-modal-body signup-terms-body">
              {termsModalLoading && (
                <div style={{ padding: 14, fontSize: 13, color: '#6b7280' }}>
                  약관 내용을 불러오는 중입니다...
                </div>
              )}
              {!termsModalLoading && !!termsModalLoadError && (
                <div style={{ padding: 14, fontSize: 13, color: '#b91c1c' }}>
                  {termsModalLoadError}
                </div>
              )}
              {!termsModalLoading && !termsModalLoadError && (
                <iframe
                  title={termsModalTitle}
                  // srcDoc 우선 적용(운영/개발 공통 안정화)
                  srcDoc={termsModalHtml}
                  // 폴백: 혹시 srcDoc이 비어있는 경우를 대비해 src도 함께 둡니다.
                  src={termsModalUrl}
                  className="signup-terms-iframe"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </SignupPageLayout>
  );
}
